import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { UIWheelSegments } from "../../common_ui_elements/ui_wheel_segments.js";
import { dailyTaskRewardIcon } from "../../common_ui_elements/reward_icon.js";
import { PlinkoLayout, PlinkoPeg } from "./plinko_layout.js";

export class PlinkoBoard extends UIElement
{
  private static readonly COIN_SIZE = 74;
  private static readonly COIN_Y = 18;
  private static readonly VALUE_Y = 80;
  private static readonly VALUE_H = 58;
  private static readonly VALUE_FONT = 52;
  private static readonly VALUE_MIN_FONT = 36;
  private static readonly VALUE_OUTLINE = 9;

  private readonly pegs: PlinkoPeg[] = PlinkoLayout.pegs();
  private readonly scale_factor: number;
  private slot_rewards: ( DailyTaskRewardDto | null )[] = [];

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.scale_factor = width / PlinkoLayout.BOARD_W;
    this.setClickable( false );
  }

  public static rewardIcon( reward: DailyTaskRewardDto ): ImgID
  {
    return reward.type === "coin" ? ImgID.IconCoin : dailyTaskRewardIcon( reward.type );
  }

  public static floatLabel( reward: DailyTaskRewardDto ): string
  {
    return reward.type === "coin" ? `+${reward.amount}` : UIWheelSegments.rewardLabel( reward );
  }

  public setSlotRewards( rewards: ( DailyTaskRewardDto | null )[] ): void
  {
    this.slot_rewards = rewards;
    this.createSlots();
    this.forceRedraw();
  }

  private createSlots(): void
  {
    const s = this.scale_factor;

    this.slot_rewards.forEach( ( reward, index ) =>
    {
      if ( !reward )
        return;

      const x = PlinkoLayout.slotLeft( index ) * s;
      const y = PlinkoLayout.FLOOR_Y * s;
      const w = ( PlinkoLayout.slotRight( index ) - PlinkoLayout.slotLeft( index ) ) * s;
      const h = PlinkoLayout.SLOTS_H * s;

      const slot = new UIElementHolder( this.ctx, x, y, w, h );
      slot.setClickable( false );
      this.addChild( slot, true );

      this.createSlotIcon( slot, reward );
      this.createSlotValue( slot, UIWheelSegments.rewardLabel( reward ) );
    } );
  }

  private createSlotIcon( slot: UIElementHolder, reward: DailyTaskRewardDto ): void
  {
    const id = PlinkoBoard.rewardIcon( reward );
    const box = PlinkoBoard.COIN_SIZE * this.scale_factor;
    const image = ImagesHolder.getLoadedImage( id );
    const size = image ? UIWheelSegments.fitSize( image, box ) : { w: box, h: box };
    const x = ( slot.getRect().getW() - size.w ) / 2;
    const y = PlinkoBoard.COIN_Y * this.scale_factor + ( box - size.h ) / 2;

    const icon = new UIImage( this.ctx, x, y, size.w, size.h, id );
    icon.setClickable( false );
    slot.addChild( icon, true );
  }

  private createSlotValue( slot: UIElementHolder, label: string ): void
  {
    const w = slot.getRect().getW();
    const h = PlinkoBoard.VALUE_H * this.scale_factor;
    const y = PlinkoBoard.VALUE_Y * this.scale_factor;

    const text = new UITextElement( this.ctx, 0, y, w, h, {
      text: label,
      font_size: LayoutScaler.textSizeFromDesign( PlinkoBoard.VALUE_FONT ),
      font_weight: 900,
      text_style: TextStyleScheme.rubik,
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.happy_fall_value_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( PlinkoBoard.VALUE_OUTLINE ),
      shadow_offset_x: 0,
      shadow_offset_y: 0,
      auto_wrap: false,
      scale_to_width: true,
      min_font_size: LayoutScaler.textSizeFromDesign( PlinkoBoard.VALUE_MIN_FONT ),
    } );
    text.setClickable( false );
    slot.addChild( text, true );
  }

  private drawImage( ctx: CanvasRenderingContext2D, id: ImgID, x: number, y: number, w: number, h: number ): void
  {
    const image = ImagesHolder.getLoadedImage( id );
    if ( image )
      ctx.drawImage( image, x, y, w, h );
  }

  private drawPegs( ctx: CanvasRenderingContext2D ): void
  {
    ctx.fillStyle = ColorScheme.happy_fall_peg;

    for ( const peg of this.pegs )
    {
      ctx.beginPath();
      ctx.arc( peg.x, peg.y, PlinkoLayout.PEG_RADIUS, 0, Math.PI * 2 );
      ctx.fill();
    }
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    const ctx = this.ctx;
    ctx.save();
    ctx.translate( -this.rect.getW2(), -this.rect.getH2() );
    ctx.scale( this.scale_factor, this.scale_factor );
    this.drawImage( ctx, ImgID.UI_HappyFallBoard, 0, 0, PlinkoLayout.BOARD_W, PlinkoLayout.BOARD_H );
    this.drawImage( ctx, ImgID.UI_HappyFallHole, PlinkoLayout.HOLE_X, PlinkoLayout.HOLE_Y, PlinkoLayout.HOLE_W, PlinkoLayout.HOLE_H );
    this.drawPegs( ctx );
    ctx.restore();

    this.finishRender();
  }

  public name(): string
  {
    return "PlinkoBoard";
  }
}
